// SimForge Baseball Players Page — PFL-inspired redesign
// Redesigned: 2026-07-22

import React, { useState, useEffect, useCallback } from 'react';
import './PlayersPage.css';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { FUNCTIONS_URL } from '../services/supabaseConfig';
import { Player, PlayerAttributes } from '../types';
import { getPlayersByUser, getRetiredPlayers, retirePlayer } from '../services/db';
import PlayerFace from '../components/PlayerFace';
import { FaceFeatures } from '../utils/faceGenerator';

// ── Helpers ──────────────────────────────────────────────────────────────

function numberToGrade(value: number | undefined | null): string {
  if (value === undefined || value === null) return '-';
  if (value >= 90) return 'S';
  if (value >= 85) return 'A+';
  if (value >= 80) return 'A';
  if (value >= 75) return 'A-';
  if (value >= 70) return 'B+';
  if (value >= 65) return 'B';
  if (value >= 60) return 'B-';
  if (value >= 55) return 'C+';
  if (value >= 50) return 'C';
  if (value >= 45) return 'C-';
  if (value >= 40) return 'D+';
  if (value >= 35) return 'D';
  if (value >= 30) return 'D-';
  return 'F';
}

function getGradeColor(grade: string): string {
  if (grade.startsWith('A')) return '#00d4aa';
  if (grade.startsWith('B')) return '#3b82f6';
  if (grade.startsWith('C')) return '#f59e0b';
  return '#94a3b8';
}

function getOverallColor(overall: number): string {
  if (overall >= 90) return '#00d4aa';
  if (overall >= 80) return '#3b82f6';
  if (overall >= 70) return '#f59e0b';
  return '#94a3b8';
}

interface PlayersPageProps {
  onPlayerClick?: (player: Player) => void;
  onNavigate?: (page: string) => void;
}

// ── Sort Options ─────────────────────────────────────────────────────────
type SortField = 'overall' | 'name' | 'age' | 'power' | 'contact' | 'speed' | 'fielding';
const SORT_OPTIONS: { value: SortField; label: string }[] = [
  { value: 'overall', label: 'Overall' },
  { value: 'name', label: 'Name' },
  { value: 'age', label: 'Age' },
  { value: 'power', label: 'Power' },
  { value: 'contact', label: 'Contact' },
  { value: 'speed', label: 'Speed' },
  { value: 'fielding', label: 'Fielding' },
];

// ── Main Component ───────────────────────────────────────────────────────
const PlayersPage: React.FC<PlayersPageProps> = ({ onPlayerClick, onNavigate }) => {
  const [activeTab, setActiveTab] = useState<'active' | 'retired' | 'unsigned' | 'incubator'>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('overall');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [positionFilter, setPositionFilter] = useState<'all' | 'hitter' | 'pitcher'>('all');
  const [viewMode, setViewMode] = useState<'list' | 'cards'>('cards');

  // Data
  const [userPlayers, setUserPlayers] = useState<Player[]>([]);
  const [retiredPlayers, setRetiredPlayers] = useState<Player[]>([]);
  const [unsignedPlayers, setUnsignedPlayers] = useState<any[]>([]);
  const [eggs, setEggs] = useState<any[]>([]);
  const [eggsLoading, setEggsLoading] = useState(false);
  const [ownedTeams, setOwnedTeams] = useState<any[]>([]);

  // Selected player for detail modal
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);

  // Sign modal
  const [signModalPlayer, setSignModalPlayer] = useState<any | null>(null);
  const [signTeamId, setSignTeamId] = useState('');
  const [signWaiveId, setSignWaiveId] = useState('');
  const [signRoster, setSignRoster] = useState<any[]>([]);
  const [signing, setSigning] = useState(false);
  const [faToast, setFaToast] = useState<string | null>(null);

  // ── Load Data ──────────────────────────────────────────────────────────
  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    const userId = localStorage.getItem('userId');
    if (!userId) return;

    // Active players
    const players = await getPlayersByUser(userId);
    const formattedActive: Player[] = players.map(p => ({
      id: p.id,
      name: `${p.firstName} ${p.lastName}`,
      firstName: p.firstName,
      lastName: p.lastName,
      position: p.position as Player['position'],
      teamId: p.userId,
      overall: p.overall,
      attributes: p.attributes as unknown as PlayerAttributes,
      age: p.age,
      height: p.height,
      weight: p.weight,
      throwingHand: p.throwingHand,
      battingHand: p.battingHand,
      face: p.face,
      avatarUrl: p.avatarUrl,
      gender: p.gender,
      nonTradeable: (p as any).nonTradeable || (p as any).non_tradeable || false,
      stats: { gamesPlayed: 0, atBats: 0, hits: 0, homeRuns: 0, rbi: 0, battingAvg: 0, ops: 0, stolenBases: 0 },
      contract: { status: 'signed', teamId: p.userId, salary: 0, duration: 0, seasonsRemaining: 0 },
      upcomingGames: []
    }));
    setUserPlayers(formattedActive);

    // Retired players
    const retired = await getRetiredPlayers(userId);
    const formattedRetired: Player[] = retired.map(p => ({
      id: p.id,
      name: `${p.firstName} ${p.lastName}`,
      firstName: p.firstName,
      lastName: p.lastName,
      position: p.position as Player['position'],
      teamId: p.userId,
      overall: p.overall,
      attributes: p.attributes as unknown as PlayerAttributes,
      age: p.age,
      height: p.height,
      weight: p.weight,
      throwingHand: p.throwingHand,
      battingHand: p.battingHand,
      face: p.face,
      avatarUrl: p.avatarUrl,
      gender: p.gender,
      stats: { gamesPlayed: 0, atBats: 0, hits: 0, homeRuns: 0, rbi: 0, battingAvg: 0, ops: 0, stolenBases: 0 },
      contract: { status: 'signed', teamId: p.userId, salary: 0, duration: 0, seasonsRemaining: 0 },
      upcomingGames: []
    }));
    setRetiredPlayers(formattedRetired);

    loadUnsignedAndTeams();
    loadEggs();
  };

  const loadUnsignedAndTeams = async () => {
    if (!supabase) return;
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id || localStorage.getItem('userId');
    if (!userId) return;
    const { data: unsigned } = await supabase.from('players')
      .select('id, first_name, last_name, position, overall, is_hitter, avatar_url, in_free_agency, career_season, gender')
      .eq('user_id', userId)
      .is('team_id', null)
      .eq('retired', false)
      .eq('is_scrub', false)
      .order('overall', { ascending: false });
    setUnsignedPlayers(unsigned || []);
    const { data: teams } = await supabase.from('teams').select('id, name, league_id, owner_id').eq('owner_id', userId);
    setOwnedTeams(teams || []);
    if (teams && teams.length > 0) setSignTeamId(teams[0].id);
  };

  const loadEggs = async () => {
    const userId = localStorage.getItem('userId');
    if (!userId || !supabase) return;
    setEggsLoading(true);
    try {
      const res = await fetch(`${FUNCTIONS_URL}/breed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${(await supabase!.auth.getSession()).data.session?.access_token}` },
        body: JSON.stringify({ action: 'list-eggs' }),
      });
      const data = await res.json();
      setEggs(data.eggs || []);
    } catch {
      const { data } = await supabase
        .from('eggs')
        .select('id, status, child_gender, hatch_season, created_at, hatched_at, child_id, mother_id, father_id, gender_revealed_at, portrait_revealed_at, portrait_path')
        .eq('owner_id', userId)
        .order('created_at', { ascending: false });
      setEggs(data || []);
    }
    setEggsLoading(false);
  };

  // ── Filtering & Sorting ────────────────────────────────────────────────
  const getDisplayPlayers = (): Player[] => {
    if (activeTab === 'retired') return retiredPlayers;
    if (activeTab === 'active') return userPlayers;
    return [];
  };

  const filteredAndSorted = useCallback((): Player[] => {
    let list = getDisplayPlayers();

    // Position filter
    if (positionFilter !== 'all') {
      list = list.filter(p => {
        const isPitcher = p.position === 'P';
        if (positionFilter === 'pitcher') return isPitcher;
        return !isPitcher;
      });
    }

    // Search
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(p =>
        p.name.toLowerCase().includes(q) || p.position.toLowerCase().includes(q)
      );
    }

    // Sort
    const sorted = [...list].sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;
      switch (sortBy) {
        case 'name': aVal = a.name; bVal = b.name; break;
        case 'age': aVal = a.age || 0; bVal = b.age || 0; break;
        case 'power': aVal = a.attributes?.power || 0; bVal = b.attributes?.power || 0; break;
        case 'contact': aVal = a.attributes?.contact || 0; bVal = b.attributes?.contact || 0; break;
        case 'speed': aVal = a.attributes?.speed || 0; bVal = b.attributes?.speed || 0; break;
        case 'fielding': aVal = a.attributes?.fielding || 0; bVal = b.attributes?.fielding || 0; break;
        default: aVal = a.overall; bVal = b.overall;
      }
      if (typeof aVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal as string) : (bVal as string).localeCompare(aVal);
      }
      return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });

    return sorted;
  }, [activeTab, userPlayers, retiredPlayers, positionFilter, searchTerm, sortBy, sortDir]);

  // ── Actions ────────────────────────────────────────────────────────────
  const handleCardClick = (player: Player) => {
    setSelectedPlayer(player);
    onPlayerClick?.(player);
  };

  const handleRetirePlayer = async () => {
    if (!selectedPlayer) return;
    await retirePlayer(selectedPlayer.id);
    setUserPlayers(prev => prev.filter(p => p.id !== selectedPlayer.id));
    setRetiredPlayers(prev => [...prev, selectedPlayer]);
    setSelectedPlayer(null);
  };

  const handleListOnMarketplace = async (playerId: string, playerName: string) => {
    const priceStr = prompt(`Set listing price for ${playerName} (in $BALLS):`);
    if (!priceStr) return;
    const price = parseInt(priceStr, 10);
    if (isNaN(price) || price < 100) { alert('Minimum price is $100 BALLS'); return; }
    const userId = localStorage.getItem('userId');
    if (!userId) { alert('Please sign in first'); return; }
    try {
      const res = await fetch(`${FUNCTIONS_URL}/marketplace`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}` },
        body: JSON.stringify({ action: 'list', playerId, price, sellerId: userId }),
      });
      const data = await res.json();
      if (data.success) alert(`Listed ${playerName} for ${price.toLocaleString()} $BALLS!`);
      else alert(data.error || 'Failed to list player');
    } catch { alert('Failed to list on marketplace'); }
  };

  const handleSignPlayer = async () => {
    if (!signModalPlayer || !signTeamId || !supabase) return;
    setSigning(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${FUNCTIONS_URL}/sign-player`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ action: 'sign', playerId: signModalPlayer.id, teamId: signTeamId, waivePlayerId: signWaiveId || undefined }),
      });
      const data = await res.json();
      if (data.error) setFaToast(`❌ ${data.error}`);
      else { setFaToast(`✅ ${signModalPlayer.first_name} ${signModalPlayer.last_name} signed!`); setSignModalPlayer(null); loadUnsignedAndTeams(); }
    } catch (e: any) { setFaToast(`❌ ${e.message}`); }
    setSigning(false);
    setTimeout(() => setFaToast(null), 4000);
  };

  const handleToggleFreeAgency = async (player: any) => {
    if (!supabase) return;
    const { data: { session } } = await supabase.auth.getSession();
    const action = player.in_free_agency ? 'leave_free_agency' : 'join_free_agency';
    const res = await fetch(`${FUNCTIONS_URL}/sign-player`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
      body: JSON.stringify({ action, playerId: player.id }),
    });
    const data = await res.json();
    if (data.error) setFaToast(`❌ ${data.error}`);
    else { setFaToast(player.in_free_agency ? '🔴 Removed from free agency' : '🟢 Added to free agency'); loadUnsignedAndTeams(); }
    setTimeout(() => setFaToast(null), 3000);
  };

  const openSignModal = async (player: any) => {
    setSignModalPlayer(player);
    setSignWaiveId('');
    if (signTeamId && supabase) {
      const { data } = await supabase.from('players').select('id, first_name, last_name, position, overall, is_scrub').eq('team_id', signTeamId).eq('retired', false).order('overall', { ascending: true });
      setSignRoster(data || []);
    }
  };

  // ── Stats summary ──────────────────────────────────────────────────────
  const activeCount = userPlayers.length;
  const retiredCount = retiredPlayers.length;
  const unsignedCount = unsignedPlayers.length;
  const incubatingCount = eggs.filter(e => e.status !== 'hatched').length;
  const avgOverall = activeCount > 0 ? Math.round(userPlayers.reduce((s, p) => s + p.overall, 0) / activeCount) : 0;

  // ════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════

  return (
    <div className="players-page">
      {/* ── Summary Bar ── */}
      <div className="pfl-summary-bar">
        <div className="pfl-summary-stat">
          <span className="pfl-summary-val">{activeCount}</span>
          <span className="pfl-summary-lbl">Active</span>
        </div>
        <div className="pfl-summary-divider" />
        <div className="pfl-summary-stat">
          <span className="pfl-summary-val" style={{ color: getOverallColor(avgOverall) }}>{numberToGrade(avgOverall)}</span>
          <span className="pfl-summary-lbl">Avg OVR</span>
        </div>
        <div className="pfl-summary-divider" />
        <div className="pfl-summary-stat">
          <span className="pfl-summary-val">{retiredCount}</span>
          <span className="pfl-summary-lbl">Retired</span>
        </div>
        <div className="pfl-summary-divider" />
        <div className="pfl-summary-stat">
          <span className="pfl-summary-val">{incubatingCount}</span>
          <span className="pfl-summary-lbl">Incubating</span>
        </div>
      </div>

      {/* ── Tab Bar ── */}
      <div className="pfl-tabs">
        <button className={`pfl-tab ${activeTab === 'active' ? 'active' : ''}`} onClick={() => setActiveTab('active')}>
          Active <span className="pfl-tab-count">{activeCount}</span>
        </button>
        <button className={`pfl-tab ${activeTab === 'retired' ? 'active' : ''}`} onClick={() => setActiveTab('retired')}>
          Retired <span className="pfl-tab-count">{retiredCount}</span>
        </button>
        <button className={`pfl-tab ${activeTab === 'unsigned' ? 'active' : ''}`} onClick={() => setActiveTab('unsigned')}>
          Unsigned <span className="pfl-tab-count">{unsignedCount}</span>
        </button>
        <button className={`pfl-tab ${activeTab === 'incubator' ? 'active' : ''}`} onClick={() => { setActiveTab('incubator'); loadEggs(); }}>
          🥚 Incubator <span className="pfl-tab-count">{incubatingCount}</span>
        </button>
      </div>

      {/* ── Content Area ── */}
      <div className="pfl-content">
        {faToast && <div className="pfl-toast">{faToast}</div>}

        {/* Active / Retired tabs share the card list */}
        {(activeTab === 'active' || activeTab === 'retired') && (
          <>
            {/* Filter bar */}
            <div className="pfl-filter-bar">
              <div className="pfl-search-wrap">
                <span className="pfl-search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Search players..."
                  className="pfl-search"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="pfl-pos-pills">
                <button className={`pfl-pill ${positionFilter === 'all' ? 'active' : ''}`} onClick={() => setPositionFilter('all')}>All</button>
                <button className={`pfl-pill ${positionFilter === 'hitter' ? 'active' : ''}`} onClick={() => setPositionFilter('hitter')}>Hitters</button>
                <button className={`pfl-pill ${positionFilter === 'pitcher' ? 'active' : ''}`} onClick={() => setPositionFilter('pitcher')}>Pitchers</button>
              </div>
              <div className="pfl-sort-wrap">
                <select className="pfl-sort-select" value={sortBy} onChange={e => setSortBy(e.target.value as SortField)}>
                  {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <button className="pfl-sort-dir" onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}>
                  {sortDir === 'desc' ? '↓' : '↑'}
                </button>
              </div>
              <div className="pfl-view-toggle">
                <button className={`pfl-view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="List view">📋</button>
                <button className={`pfl-view-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Card view">🃏</button>
              </div>
            </div>

            {/* Player Cards */}
            {filteredAndSorted().length === 0 ? (
              <div className="pfl-empty">
                <div className="pfl-empty-icon">⚾</div>
                <p>{searchTerm ? 'No players match your search' : 'No players here yet'}</p>
                {!searchTerm && activeTab === 'active' && (
                  <button className="pfl-create-btn" onClick={() => onNavigate?.('create-player')}>+ Create Player</button>
                )}
              </div>
            ) : viewMode === 'list' ? (
              <div className="pfl-card-list">
                {filteredAndSorted().map(player => (
                  <PlayerListCard
                    key={player.id}
                    player={player}
                    onClick={() => handleCardClick(player)}
                  />
                ))}
              </div>
            ) : (
              <div className="pfl-card-grid">
                {filteredAndSorted().map(player => (
                  <PlayerBaseballCard
                    key={player.id}
                    player={player}
                    onClick={() => handleCardClick(player)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Unsigned tab */}
        {activeTab === 'unsigned' && (
          <div className="pfl-unsigned-list">
            {unsignedPlayers.length === 0 ? (
              <div className="pfl-empty">
                <div className="pfl-empty-icon">📋</div>
                <p>No unsigned players. Hatched eggs will appear here.</p>
              </div>
            ) : (
              unsignedPlayers.map(p => (
                <div key={p.id} className="pfl-unsigned-card">
                  <div className="pfl-unsigned-portrait">
                    {p.avatar_url
                      ? <img src={p.avatar_url} alt="" />
                      : <span className="pfl-unsigned-placeholder">{p.position === 'P' ? '⚾' : '🏏'}</span>
                    }
                  </div>
                  <div className="pfl-unsigned-info">
                    <div className="pfl-unsigned-name">{p.first_name} {p.last_name}</div>
                    <div className="pfl-unsigned-meta">
                      <span className="pfl-unsigned-pos">{p.position}</span>
                      <span className="pfl-unsigned-ovr" style={{ color: getOverallColor(p.overall) }}>{numberToGrade(p.overall)}</span>
                      <span className="pfl-unsigned-season">S{p.career_season}</span>
                      {p.in_free_agency && <span className="pfl-unsigned-fa">IN FA</span>}
                    </div>
                  </div>
                  <div className="pfl-unsigned-actions">
                    {ownedTeams.length > 0 && (
                      <button className="pfl-btn-sign" onClick={() => openSignModal(p)}>➕ Sign</button>
                    )}
                    <button
                      className={p.in_free_agency ? 'pfl-btn-fa-remove' : 'pfl-btn-fa-add'}
                      onClick={() => handleToggleFreeAgency(p)}
                    >
                      {p.in_free_agency ? '🔴 Remove FA' : '🟢 Join FA'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Incubator tab */}
        {activeTab === 'incubator' && <IncubatorTab eggs={eggs} loading={eggsLoading} onNavigate={onNavigate} />}
      </div>

      {/* ── Sign Modal ── */}
      {signModalPlayer && (
        <div className="pfl-modal-overlay" onClick={() => setSignModalPlayer(null)}>
          <div className="pfl-modal" onClick={e => e.stopPropagation()}>
            <div className="pfl-modal-title">Sign {signModalPlayer.first_name} {signModalPlayer.last_name}</div>
            {ownedTeams.length > 1 && (
              <div className="pfl-modal-field">
                <label>Select team</label>
                <select value={signTeamId} onChange={async e => {
                  setSignTeamId(e.target.value);
                  if (supabase) {
                    const { data } = await supabase.from('players').select('id, first_name, last_name, position, overall, is_scrub').eq('team_id', e.target.value).eq('retired', false).order('overall', { ascending: true });
                    setSignRoster(data || []);
                  }
                }}>
                  {ownedTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
            {signRoster.length >= 25 && (
              <div className="pfl-modal-field">
                <label className="pfl-warn">⚠️ Roster full — waive a player</label>
                <select value={signWaiveId} onChange={e => setSignWaiveId(e.target.value)}>
                  <option value=''>— Pick player to waive —</option>
                  {signRoster.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name} | {p.position} | {numberToGrade(p.overall)}{p.is_scrub ? ' (scrub)' : ''}</option>)}
                </select>
              </div>
            )}
            <div className="pfl-modal-actions">
              <button className="pfl-btn-cancel" onClick={() => setSignModalPlayer(null)}>Cancel</button>
              <button className="pfl-btn-confirm" onClick={handleSignPlayer} disabled={signing || (signRoster.length >= 25 && !signWaiveId)}>
                {signing ? 'Signing...' : '✅ Sign to Team'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Player Detail Modal ── */}
      {selectedPlayer && (
        <PlayerDetailModal
          player={selectedPlayer}
          onClose={() => setSelectedPlayer(null)}
          onRetire={handleRetirePlayer}
          onNavigate={onNavigate}
          onListMarketplace={handleListOnMarketplace}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PLAYER LIST CARD — PFL-style horizontal card
// ═══════════════════════════════════════════════════════════════════════════
const PlayerListCard: React.FC<{ player: Player; onClick: () => void }> = ({ player, onClick }) => {
  const isPitcher = player.position === 'P';
  const grade = numberToGrade(player.overall);
  const attrs = isPitcher
    ? [['VEL', player.attributes?.velocity], ['CTL', player.attributes?.control], ['MOV', player.attributes?.movement]]
    : [['PWR', player.attributes?.power], ['CON', player.attributes?.contact], ['SPD', player.attributes?.speed]];

  return (
    <div className="pfl-card" onClick={onClick}>
      {/* Portrait */}
      <div className="pfl-card-portrait">
        {player.avatarUrl ? (
          <img src={player.avatarUrl} alt="" />
        ) : (
          <PlayerFace face={player.face as FaceFeatures | undefined} size={64} />
        )}
      </div>

      {/* Info block */}
      <div className="pfl-card-info">
        <div className="pfl-card-top-row">
          <span className="pfl-card-name">{player.name}</span>
          {(player as any).gender && (
            <span className={`pfl-card-gender ${(player as any).gender}`}>{(player as any).gender === 'female' ? '♀' : '♂'}</span>
          )}
        </div>
        <div className="pfl-card-meta">
          <span className="pfl-card-pos">{player.position}</span>
          <span className="pfl-card-age">Age {player.age ?? '?'}</span>
          {player.nonTradeable && <span className="pfl-card-locked">🔒</span>}
        </div>
        <div className="pfl-card-attrs">
          {attrs.map(([label, val]) => (
            <div key={label as string} className="pfl-card-attr">
              <span className="pfl-card-attr-lbl">{label}</span>
              <span className="pfl-card-attr-val">{numberToGrade(val as number)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* OVR badge */}
      <div className="pfl-card-ovr">
        <span className="pfl-card-ovr-grade" style={{ color: getGradeColor(grade) }}>{grade}</span>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PLAYER BASEBALL CARD — trading card grid view
// ═══════════════════════════════════════════════════════════════════════════
const PlayerBaseballCard: React.FC<{ player: Player; onClick: () => void }> = ({ player, onClick }) => {
  const isPitcher = player.position === 'P';
  const grade = numberToGrade(player.overall);
  const ovrColor = getOverallColor(player.overall);
  const attrs = isPitcher
    ? [['VEL', player.attributes?.velocity], ['CTL', player.attributes?.control], ['MOV', player.attributes?.movement], ['DEC', player.attributes?.deception]]
    : [['PWR', player.attributes?.power], ['CON', player.attributes?.contact], ['SPD', player.attributes?.speed], ['FLD', player.attributes?.fielding]];

  return (
    <div className="bbc-card" onClick={onClick}>
      {/* Top band: position + OVR */}
      <div className="bbc-top">
        <span className="bbc-pos">{player.position}</span>
        <div className="bbc-ovr-box">
          <span className="bbc-ovr-label">OVR</span>
          <span className="bbc-ovr-grade">{grade}</span>
        </div>
      </div>

      {/* Portrait — big */}
      <div className="bbc-portrait">
        {player.avatarUrl ? (
          <img src={player.avatarUrl} alt="" />
        ) : (
          <div className="bbc-portrait-face">
            <PlayerFace face={player.face as FaceFeatures | undefined} size={140} />
          </div>
        )}
        <div className="bbc-portrait-fade" />
      </div>

      {/* Name strip */}
      <div className="bbc-name">
        {player.name}
        {(player as any).gender && (
          <span className={`bbc-gender ${(player as any).gender}`}>{(player as any).gender === 'female' ? '♀' : '♂'}</span>
        )}
      </div>

      {/* Attribute chips */}
      <div className="bbc-attrs">
        {attrs.map(([label, val]) => {
          const g = numberToGrade(val as number);
          return (
            <div key={label as string} className="bbc-attr">
              <span className="bbc-attr-lbl">{label}</span>
              <span className="bbc-attr-val" style={{ color: getGradeColor(g) }}>{g}</span>
            </div>
          );
        })}
      </div>

      {/* Footer: age + grade only */}
      <div className="bbc-footer">
        <span className="bbc-age">Age {player.age ?? '?'}</span>
        <span className="bbc-ovr-num" style={{ color: ovrColor }}>{grade}</span>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PLAYER DETAIL MODAL — full profile view
// ═══════════════════════════════════════════════════════════════════════════
const PlayerDetailModal: React.FC<{
  player: Player;
  onClose: () => void;
  onRetire: () => void;
  onNavigate?: (page: string) => void;
  onListMarketplace: (id: string, name: string) => void;
}> = ({ player, onClose, onRetire, onNavigate, onListMarketplace }) => {
  const isPitcher = player.position === 'P';

  const hitterAttrs = [
    ['Power', player.attributes?.power],
    ['Contact', player.attributes?.contact],
    ['Speed', player.attributes?.speed],
    ['Fielding', player.attributes?.fielding],
    ['Arm', player.attributes?.arm],
    ['Discipline', player.attributes?.discipline],
  ];
  const pitcherAttrs = [
    ['Velocity', player.attributes?.velocity],
    ['Control', player.attributes?.control],
    ['Movement', player.attributes?.movement],
    ['Deception', player.attributes?.deception],
    ['Endurance', player.attributes?.endurance],
  ];
  const attrList = isPitcher ? pitcherAttrs : hitterAttrs;

  return (
    <div className="pfl-modal-overlay" onClick={onClose}>
      <div className="pfl-detail" onClick={e => e.stopPropagation()}>
        <button className="pfl-detail-close" onClick={onClose}>✕</button>

        {/* Header: portrait + name + grade */}
        <div className="pfl-detail-header">
          <div className="pfl-detail-portrait">
            {player.avatarUrl ? (
              <img src={player.avatarUrl} alt="" />
            ) : (
              <PlayerFace face={player.face as FaceFeatures | undefined} size={80} />
            )}
          </div>
          <div className="pfl-detail-title">
            <h2>{player.name}</h2>
            <div className="pfl-detail-subtitle">
              <span className="pfl-detail-pos">{player.position}</span>
              <span className="pfl-detail-age">Age {player.age ?? '?'}</span>
              {(player as any).gender && (
                <span className={`pfl-detail-gender ${(player as any).gender}`}>{(player as any).gender === 'female' ? '♀ Female' : '♂ Male'}</span>
              )}
            </div>
          </div>
          <div className="pfl-detail-ovr">
            <span className="pfl-detail-ovr-grade" style={{ color: getGradeColor(numberToGrade(player.overall)) }}>
              {numberToGrade(player.overall)}
            </span>
          </div>
        </div>

        {/* Attributes grid */}
        <div className="pfl-detail-section">
          <h3>{isPitcher ? 'Pitching Attributes' : 'Batting Attributes'}</h3>
          <div className="pfl-detail-attrs">
            {attrList.map(([label, val]) => {
              const grade = numberToGrade(val as number);
              return (
                <div key={label as string} className="pfl-detail-attr">
                  <span className="pfl-detail-attr-lbl">{label}</span>
                  <span className="pfl-detail-attr-grade" style={{ color: getGradeColor(grade) }}>{grade}</span>
                  <div className="pfl-detail-attr-bar">
                    <div className="pfl-detail-attr-fill" style={{
                      width: `${Math.min(((val as number) || 0) / 99 * 100, 100)}%`,
                      background: getGradeColor(grade)
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action buttons */}
        <div className="pfl-detail-actions">
          {player.nonTradeable ? (
            <button className="pfl-btn-locked" disabled>🔒 Non-Tradeable</button>
          ) : (
            <button className="pfl-btn-sell" onClick={() => onListMarketplace(player.id, player.name)}>
              💰 List on Marketplace
            </button>
          )}
          {!isPitcher && (player as any).gender === 'female' && (
            <button className="pfl-btn-forge" onClick={() => onNavigate?.(`/forge?femaleId=${player.id}`)}>
              🧬 Forge
            </button>
          )}
          {!isPitcher && (player as any).gender === 'male' && (
            <button className="pfl-btn-stud" onClick={() => onNavigate?.(`/player/${player.id}`)}>
              🐂 List as Stud
            </button>
          )}
          <button className="pfl-btn-retire" onClick={onRetire}> Retire</button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// INCUBATOR TAB — egg pipeline view (preserved from original)
// ═══════════════════════════════════════════════════════════════════════════
const IncubatorTab: React.FC<{
  eggs: any[];
  loading: boolean;
  onNavigate?: (page: string) => void;
}> = ({ eggs, loading, onNavigate }) => {
  const [selectedEgg, setSelectedEgg] = useState<{ egg: any; mother: any; father: any } | null>(null);

  const incubating = eggs.filter(e => e.status === 'incubating' && !e.gender_revealed_at);
  const genderRevealed = eggs.filter(e => e.status === 'incubating' && e.gender_revealed_at && !e.portrait_revealed_at);
  const portraitRevealed = eggs.filter(e => e.status === 'incubating' && e.portrait_revealed_at && !e.child_id);
  const babies = eggs.filter(e => e.status === 'baby');

  const GENDER_COLOR: Record<string, string> = { male: '#60a5fa', female: '#f472b6' };

  const showAncestry = async (egg: any) => {
    const { supabase } = await import('../services/supabase');
    const { data: mother } = await supabase.from('players').select('*').eq('id', egg.mother_id).single();
    const { data: father } = await supabase.from('players').select('*').eq('id', egg.father_id).single();
    setSelectedEgg({ egg, mother, father });
  };

  const EggCard: React.FC<{ egg: any; stage: string }> = ({ egg, stage }) => {
    const icon = stage === 'incubating' ? '🥚' : stage === 'gender' ? (egg.child_gender === 'female' ? '🥚♀️' : '🥚♂️') : stage === 'portrait' ? (egg.child_gender === 'female' ? '🖼️♀️' : '🖼️♂️') : '🐣';
    const badge = stage === 'incubating' ? '❓ Unknown' : stage === 'gender' ? (egg.child_gender === 'female' ? '♀ Female' : '♂ Male') : stage === 'portrait' ? 'Portrait Ready' : 'Baby';
    const badgeColor = stage === 'incubating' ? '#d4a017' : GENDER_COLOR[egg.child_gender] || '#d4a017';
    const info = stage === 'incubating'
      ? `Gender reveal season ${egg.hatch_season - 2} · Hatches season ${egg.hatch_season}`
      : stage === 'gender'
        ? `Portrait reveal season ${egg.hatch_season - 1} · Hatches season ${egg.hatch_season}`
        : stage === 'portrait'
          ? `Ready to hatch season ${egg.hatch_season}`
          : `Hatches season ${egg.hatch_season}`;

    return (
      <div className="pfl-egg-card" style={{ borderColor: badgeColor }} onClick={() => showAncestry(egg)}>
        <span className="pfl-egg-icon">{icon}</span>
        <div className="pfl-egg-info">
          <span className="pfl-egg-badge" style={{ background: badgeColor, color: stage === 'incubating' ? '#060a12' : '#fff' }}>{badge}</span>
          <span className="pfl-egg-meta">{info}</span>
          <span className="pfl-egg-date">Created {new Date(egg.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="pfl-incubator">
      {loading ? (
        <div className="pfl-empty"><p>Loading eggs...</p></div>
      ) : eggs.length === 0 ? (
        <div className="pfl-empty">
          <div className="pfl-empty-icon">🥚</div>
          <p>No eggs yet.</p>
          <p className="pfl-empty-sub">Head to <strong style={{ color: '#e8b82a' }}>Player Forge</strong> → Breed to create one.</p>
        </div>
      ) : (
        <>
          {incubating.length > 0 && (
            <div className="pfl-egg-section">
              <h3 className="pfl-egg-section-title">⏳ Incubating ({incubating.length})</h3>
              {incubating.map(egg => <EggCard key={egg.id} egg={egg} stage="incubating" />)}
            </div>
          )}
          {genderRevealed.length > 0 && (
            <div className="pfl-egg-section">
              <h3 className="pfl-egg-section-title">👶 Gender Revealed ({genderRevealed.length})</h3>
              {genderRevealed.map(egg => <EggCard key={egg.id} egg={egg} stage="gender" />)}
            </div>
          )}
          {portraitRevealed.length > 0 && (
            <div className="pfl-egg-section">
              <h3 className="pfl-egg-section-title">🖼️ Portrait Revealed ({portraitRevealed.length})</h3>
              {portraitRevealed.map(egg => <EggCard key={egg.id} egg={egg} stage="portrait" />)}
            </div>
          )}
          {babies.length > 0 && (
            <div className="pfl-egg-section">
              <h3 className="pfl-egg-section-title">🐣 Babies ({babies.length})</h3>
              {babies.map(egg => (
                <div key={egg.id} className="pfl-egg-card" style={{ borderColor: GENDER_COLOR[egg.child_gender] || '#d4a017' }}>
                  <span className="pfl-egg-icon">🐣</span>
                  <div className="pfl-egg-info">
                    <span className="pfl-egg-badge" style={{ background: GENDER_COLOR[egg.child_gender] || '#8b949e', color: '#fff' }}>
                      {egg.child_gender === 'female' ? '♀' : '♂'} Baby
                    </span>
                    <span className="pfl-egg-meta">Hatches season {egg.hatch_season}</span>
                    {egg.child_id && (
                      <button className="pfl-egg-view-btn" onClick={() => onNavigate?.(`/player/${egg.child_id}`)}>View Player</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Egg Bloodline Modal */}
      {selectedEgg && (
        <div className="pfl-modal-overlay" onClick={() => setSelectedEgg(null)}>
          <div className="pfl-modal" onClick={e => e.stopPropagation()}>
            <div className="pfl-modal-title">🥚 Egg Bloodline</div>
            <div className="pfl-egg-bloodline-info">
              <span style={{ color: GENDER_COLOR[selectedEgg.egg.child_gender] || '#d4a017', fontSize: 14, fontWeight: 700 }}>
                {selectedEgg.egg.child_gender === 'male' ? '♂' : '♀'} Mystery
              </span>
              <span style={{ color: '#8b949e', fontSize: 13 }}>Hatch S{selectedEgg.egg.hatch_season}</span>
            </div>
            {selectedEgg.mother && selectedEgg.father ? (
              <div className="pfl-egg-parents">
                <div className="pfl-egg-parent" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: `/player/${selectedEgg.mother.id}` }))}>
                  <span className="pfl-egg-parent-name">{selectedEgg.mother.first_name} {selectedEgg.mother.last_name}</span>
                  <span className="pfl-egg-parent-role">👩 Mother</span>
                  <span className="pfl-egg-parent-stats">{selectedEgg.mother.position} · {selectedEgg.mother.overall} OVR</span>
                </div>
                <div className="pfl-egg-parent" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: `/player/${selectedEgg.father.id}` }))}>
                  <span className="pfl-egg-parent-name">{selectedEgg.father.first_name} {selectedEgg.father.last_name}</span>
                  <span className="pfl-egg-parent-role">👨 Father</span>
                  <span className="pfl-egg-parent-stats">{selectedEgg.father.position} · {selectedEgg.father.overall} OVR</span>
                </div>
              </div>
            ) : (
              <p style={{ color: '#8b949e', textAlign: 'center', padding: 20 }}>Loading parent data...</p>
            )}
            <button className="pfl-btn-cancel" onClick={() => setSelectedEgg(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayersPage;
